import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { adminAPI } from '../../api/client'
import AdminEmptyState from './AdminEmptyState'
import AdminPagination from './AdminPagination'
import AdminSkeleton from './AdminSkeleton'
import type { AdminAuditRow } from './adminTypes'
import { exportCsv, formatWhen } from './adminUtils'

type Props = {
  token: string
}

export default function AdminAuditPanel({ token }: Props) {
  const { t, i18n } = useTranslation()
  const [items, setItems] = useState<AdminAuditRow[]>([])
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const pageSize = 50

  const load = useCallback(() => {
    setLoading(true)
    adminAPI
      .auditLog(token, { q: q.trim() || undefined, skip: (page - 1) * pageSize, limit: pageSize })
      .then((res) => {
        setItems((res.data?.items as AdminAuditRow[]) ?? [])
        setTotal(Number(res.data?.total ?? 0))
      })
      .finally(() => setLoading(false))
  }, [token, q, page])

  useEffect(() => {
    const tmr = window.setTimeout(load, q ? 250 : 0)
    return () => window.clearTimeout(tmr)
  }, [load, q])

  useEffect(() => {
    setPage(1)
  }, [q])

  return (
    <div className="vc-admin-panel vc-admin-panel--card">
      <header className="vc-admin-panel__head">
        <div>
          <h2>{t('admin.nav.audit')}</h2>
          <p className="vc-admin-panel__hint">{t('admin.auditIntro')}</p>
        </div>
        <button
          type="button"
          className="vc-admin-btn vc-admin-btn--ghost"
          onClick={() =>
            exportCsv(
              'admin-audit.csv',
              ['when', 'admin', 'action', 'target', 'metadata'],
              items.map((row) => [
                row.created_at ?? '',
                row.admin_email,
                row.action,
                `${row.target_type}:${row.target_id ?? ''}`,
                JSON.stringify(row.metadata ?? {}),
              ]),
            )
          }
        >
          {t('admin.exportCsv')}
        </button>
      </header>
      <input
        type="search"
        className="vc-admin-input vc-admin-input--search"
        placeholder={t('admin.searchAudit')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {loading && items.length === 0 ? <AdminSkeleton variant="table" rows={6} /> : null}
      {!loading && items.length === 0 ? (
        <AdminEmptyState title={t('admin.noAudit')} hint={t('admin.auditIntro')} />
      ) : null}
      {items.length > 0 ? (
        <>
          <div className="vc-admin-table-wrap">
            <table className="vc-admin-table">
              <thead>
                <tr>
                  <th>{t('admin.created')}</th>
                  <th>{t('admin.adminActor')}</th>
                  <th>{t('admin.action')}</th>
                  <th>{t('admin.target')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id}>
                    <td>{formatWhen(row.created_at, i18n.language)}</td>
                    <td>{row.admin_email}</td>
                    <td>{t(`admin.auditAction.${row.action}`, { defaultValue: row.action })}</td>
                    <td>
                      {row.target_type}
                      {row.target_id != null ? ` #${row.target_id}` : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AdminPagination page={page} pageSize={pageSize} total={total} onChange={setPage} />
        </>
      ) : null}
    </div>
  )
}
